/**
 * Where is the portal being opened? Two questions are answered here, from the
 * same handful of facts about the browser, so nothing sniffs a user agent
 * twice:
 *
 *   classifyInstallContext  may the enrolment link's one-time token be spent
 *                           right here, or must the Home Screen icon spend it?
 *   installOfferFor         is there anything to say about putting this page
 *                           on the Home Screen, and does the browser give us
 *                           a real button for it?
 *
 * THE ENROLMENT LINK (a driver)
 * -----------------------------
 *
 * On iPhone and iPad a Home Screen web app gets its OWN cookies and storage,
 * separate from Safari. A token exchanged in Safari signs Safari in, and the
 * icon the driver adds next opens signed out, with the link already spent.
 * So on iOS the icon itself has to be the one that exchanges the token
 * (see Enrolment.jsx). Android Chrome shares storage with the installed app,
 * so everything that is not iOS keeps today's flow untouched.
 *
 *   ios-safari         real Safari on iPhone/iPad: add to Home Screen first
 *   ios-other-browser  Chrome/Firefox/Edge/… or an in-app browser on iOS:
 *                      ask for Safari (only Safari reliably offers the
 *                      "Add to Home Screen" item this flow depends on)
 *   ios-standalone     running from the Home Screen icon: exchange now
 *   other              Android, desktop, anything else: unchanged
 *
 * Deliberately explicit rather than clever: a flat list of tokens, checked in
 * a fixed order, each with a test (installContext.test.js).
 */

export const INSTALL_CONTEXTS = Object.freeze({
  IOS_SAFARI: 'ios-safari',
  IOS_OTHER_BROWSER: 'ios-other-browser',
  IOS_STANDALONE: 'ios-standalone',
  OTHER: 'other',
});

// iPhone, iPad and iPod say so in the user agent.
const IOS_DEVICE = /iPhone|iPad|iPod/;

// Since iPadOS 13 an iPad's Safari asks for desktop sites with a Mac user
// agent. The Mac has no touch screen, so touch points give the iPad away.
const MAC_UA = /Macintosh/;

// The only other thing with a Home Screen. Everything else is a desk.
const ANDROID_UA = /Android/i;

// Other browsers on iOS. They all run WebKit and look like Safari apart from
// this token.
const OTHER_BROWSER_TOKENS = [
  'CriOS', // Chrome
  'FxiOS', // Firefox
  'EdgiOS', // Edge (also carries Version/ and Safari/, so check it first)
  'OPiOS', // Opera
  'OPT/', // Opera Touch
  'YaBrowser', // Yandex
  'DuckDuckGo', // DuckDuckGo
  'GSA/', // the Google app
];

// In-app browsers: links tapped inside another app's feed or chat.
const IN_APP_TOKENS = [
  'FBAN', // Facebook, Messenger
  'FBAV', // Facebook
  'Instagram',
  'Line/',
  'MicroMessenger', // WeChat
  'Snapchat',
  'LinkedInApp',
  'Pinterest',
  'musical_ly', // TikTok
  'BytedanceWebview', // TikTok
  'Twitter',
  // WhatsApp on iOS hands links to the default browser, so a driver tapping
  // the link in WhatsApp normally lands in Safari. Listed in case a build
  // ever opens links in-app and says so.
  'WhatsApp',
];

// Real Safari says both. A bare WKWebView (the Home Screen app, most in-app
// browsers) has neither.
const SAFARI_MARKERS = [/Version\/[\d.]+/, /Safari\/[\d.]+/];

const hasAnyToken = (userAgent, tokens) => tokens.some((token) => userAgent.includes(token));

/** True for iPhone/iPad/iPod, including an iPad sending a desktop Mac UA. */
export const isIOSDevice = ({ userAgent = '', maxTouchPoints = 0 } = {}) => (
  IOS_DEVICE.test(userAgent) || (MAC_UA.test(userAgent) && maxTouchPoints > 1)
);

/**
 * Is this a phone or a tablet — something with a Home Screen and a person
 * walking about with it — rather than a desk?
 *
 * The one question behind "not on desktop": a dispatcher at a desk is not
 * offered an app to install and is not asked for notifications on a machine
 * they will leave at six. Deliberately coarse, and deliberately not a
 * screen-width test: a narrow window on a laptop is still a desk.
 */
export const isHandheldDevice = ({ userAgent = '', maxTouchPoints = 0 } = {}) => (
  isIOSDevice({ userAgent, maxTouchPoints }) || ANDROID_UA.test(userAgent)
);

/** Is the page this code is running in on a phone or a tablet? */
export const isHandheld = (win = typeof window === 'undefined' ? undefined : window) => {
  const nav = win?.navigator;

  return nav ? isHandheldDevice({
    userAgent: nav.userAgent || '',
    maxTouchPoints: Number(nav.maxTouchPoints) || 0,
  }) : false;
};

/**
 * The pure classification, from plain values, so every row of the table is
 * testable without faking a browser.
 *
 *   userAgent       navigator.userAgent
 *   maxTouchPoints  navigator.maxTouchPoints
 *   standalone      navigator.standalone === true, or display-mode: standalone
 */
export function classifyInstallContext({
  userAgent = '', maxTouchPoints = 0, standalone = false,
} = {}) {
  if (!isIOSDevice({ userAgent, maxTouchPoints })) return INSTALL_CONTEXTS.OTHER;

  // First: the Home Screen app's UA has no Safari marker, and would otherwise
  // look like an unknown in-app browser.
  if (standalone) return INSTALL_CONTEXTS.IOS_STANDALONE;

  if (hasAnyToken(userAgent, OTHER_BROWSER_TOKENS)) return INSTALL_CONTEXTS.IOS_OTHER_BROWSER;
  if (hasAnyToken(userAgent, IN_APP_TOKENS)) return INSTALL_CONTEXTS.IOS_OTHER_BROWSER;

  if (SAFARI_MARKERS.every((marker) => marker.test(userAgent))) return INSTALL_CONTEXTS.IOS_SAFARI;

  // Some other WebKit view we cannot name. Safer to send the driver to Safari
  // (with a way to carry on here) than to spend the link somewhere that may
  // not be able to add it to the Home Screen.
  return INSTALL_CONTEXTS.IOS_OTHER_BROWSER;
}

const matchesStandalone = (win) => {
  try {
    return Boolean(win.matchMedia && win.matchMedia('(display-mode: standalone)').matches);
  } catch {
    return false;
  }
};

/**
 * Is this page already running as an installed app — the Home Screen icon on
 * iOS, the installed PWA anywhere else? The one check every "put me on the
 * Home Screen" card has to make before it says anything at all.
 */
export const isInstalled = (win = typeof window === 'undefined' ? undefined : window) => (
  win?.navigator?.standalone === true || (win ? matchesStandalone(win) : false)
);

/** Everything about the browser the two classifications below read. */
const factsOf = (win) => {
  const nav = win?.navigator;
  if (!nav) return null;
  return {
    userAgent: nav.userAgent || '',
    maxTouchPoints: Number(nav.maxTouchPoints) || 0,
    standalone: isInstalled(win),
  };
};

/** Classifies the page this code is running in. */
export function detectInstallContext(win = typeof window === 'undefined' ? undefined : window) {
  const facts = factsOf(win);
  return facts ? classifyInstallContext(facts) : INSTALL_CONTEXTS.OTHER;
}

/* --- offering the Home Screen to somebody who is already signed in --------
 *
 * The driver's enrolment screen has to GATE on this (the icon spends the
 * link). Everybody else — office staff on the dispatch board — is merely
 * being offered something, so the question is narrower: is there an honest
 * thing to show, and does the browser hand us a real button?
 *
 * Deliberately a different answer from `classifyInstallContext`, because
 * "already installed" and "this is a desk" both mean NOTHING AT ALL here and
 * neither is a row in that table.
 */

export const INSTALL_OFFERS = Object.freeze({
  /** Say nothing: already installed, or a desktop, which has no Home Screen. */
  NONE: 'none',
  /** `beforeinstallprompt` has fired — offer the browser's own install button. */
  PROMPT: 'prompt',
  /** iPhone/iPad in Safari: Share → "Add to Home Screen", by hand. */
  IOS_STEPS: 'ios-steps',
  /** iPhone/iPad anywhere else: only Safari reliably offers that item. */
  IOS_SAFARI_FIRST: 'ios-safari-first',
  /** Android without a prompt (Firefox, Samsung Internet): the menu, by hand. */
  ANDROID_STEPS: 'android-steps',
});

/**
 * What, if anything, to offer — from plain values, so every row is testable
 * without faking a browser.
 *
 *   standalone  already installed: there is nothing to add
 *   canPrompt   a `beforeinstallprompt` event is in hand
 *
 * A DESKTOP IS `NONE`, even though desktop Chrome fires
 * `beforeinstallprompt` as happily as Android's. A dispatcher at a desk did
 * not ask for an app, and this offer must never be in the way of the board;
 * a Home Screen is a phone's.
 */
export function installOfferFor({
  userAgent = '', maxTouchPoints = 0, standalone = false, canPrompt = false,
} = {}) {
  if (standalone) return INSTALL_OFFERS.NONE;

  const context = classifyInstallContext({ userAgent, maxTouchPoints, standalone });

  if (context === INSTALL_CONTEXTS.IOS_SAFARI) return INSTALL_OFFERS.IOS_STEPS;
  if (context === INSTALL_CONTEXTS.IOS_OTHER_BROWSER) return INSTALL_OFFERS.IOS_SAFARI_FIRST;

  if (!ANDROID_UA.test(userAgent)) return INSTALL_OFFERS.NONE;

  return canPrompt ? INSTALL_OFFERS.PROMPT : INSTALL_OFFERS.ANDROID_STEPS;
}

/** What to offer in the page this code is running in. */
export function detectInstallOffer(
  win = typeof window === 'undefined' ? undefined : window,
  { canPrompt = false } = {},
) {
  const facts = factsOf(win);
  return facts ? installOfferFor({ ...facts, canPrompt }) : INSTALL_OFFERS.NONE;
}
